import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';
import { Input } from '@antimatterhq/ui/components/input';
import { Label } from '@antimatterhq/ui/components/label';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupProps,
} from '@antimatterhq/ui/components/radioGroup';
import { FormMessage } from '@/components/form/form';
import { ComputeGroupSchemaFormValues } from '@/zod/schemas/ComputeGroupSchema';
import {
  computeAutomaticTooltipRl,
  computeDedicatedTooltipRl,
  computeGroupTooltipRl,
} from '@/strings/StringsTooltips';

export type ComputeResourcesRadioGroupProps = {
  handleChange: ReturnType<typeof useFormik>['handleChange'];
  handleBlur: ReturnType<typeof useFormik>['handleBlur'];
  values: ComputeGroupSchemaFormValues;
  errors: FormikErrors<ComputeGroupSchemaFormValues>;
  touched: FormikTouched<ComputeGroupSchemaFormValues>;
} & Pick<RadioGroupProps, 'disabled'>;

export default function ComputeResourcesRadioGroup({
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  disabled,
}: ComputeResourcesRadioGroupProps) {
  return (
    <RadioGroup
      id="compute-resources"
      name="compute-resources"
      value={values.computeResource}
      onValueChange={(value) =>
        handleChange({ target: { name: 'computeResource', value } })
      }
      disabled={disabled}
      data-testid="compute-resources-radio-group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <RadioGroupItem
            value="automatic"
            id="automatic-radio"
            aria-labelledby="automatic-radio-label"
            data-testid="automatic-radio-item"
          />
          <Label
            htmlFor="automatic-radio"
            id="automatic-radio-label"
            className="text-grey-800 dark:text-grey-100"
            mb={0}
          >
            Automatic
          </Label>
        </div>
        <div className="h-[40px]"></div>
        <InfoTooltip content={computeAutomaticTooltipRl} />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <RadioGroupItem
            value="dedicated"
            id="dedicated-radio"
            aria-labelledby="dedicated-radio-label"
            data-testid="dedicated-radio-item"
          />
          <Label
            htmlFor="dedicated-radio"
            id="dedicated-radio-label"
            className="text-grey-800 dark:text-grey-100"
            mb={0}
          >
            Dedicated
          </Label>
        </div>
        <div className="h-[40px]"></div>
        <InfoTooltip content={computeDedicatedTooltipRl} />
      </div>

      <div className="flex items-center space-x-2">
        <div className="flex w-32 items-center space-x-2">
          <RadioGroupItem
            value="group"
            id="group-radio"
            aria-labelledby="group-radio-label"
            data-testid="group-radio-item"
          />
          <Label
            htmlFor="group-radio"
            id="group-radio-label"
            className="text-grey-800 dark:text-grey-100"
            mb={0}
          >
            Assign to group:
          </Label>
        </div>
        <div className="flex-grow">
          <Input
            id="group-name"
            name="group-name"
            value={values.computeResourceGroup ?? undefined}
            disabled={values.computeResource !== 'group' || disabled}
            onChange={(e) =>
              handleChange({
                target: { name: 'computeResourceGroup', value: e.target.value },
              })
            }
            onBlur={() =>
              handleBlur({ target: { name: 'computeResourceGroup' } })
            }
            data-testid="group-name-input"
          />
        </div>
        <InfoTooltip content={computeGroupTooltipRl} />
      </div>
      {!!errors.computeResourceGroup && touched.computeResourceGroup && (
        <div className="pl-32">
          <FormMessage type="error">{errors.computeResourceGroup}</FormMessage>
        </div>
      )}
    </RadioGroup>
  );
}
